import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, Phone, MessageCircle, Award, Play } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import { serviceCategories } from '../data/serviceCategories';
import { serviceProviders } from '../data/serviceProviders';

const ServiceCategoryPage = () => {
  const { country, categorySlug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [providers, setProviders] = useState([]);
  const [sortBy, setSortBy] = useState('rating');
  const [filterCity, setFilterCity] = useState('');

  useEffect(() => {
    // Encontrar categoria
    const foundCategory = serviceCategories.find(cat => cat.slug === categorySlug);
    setCategory(foundCategory);

    if (foundCategory) {
      // Filtrar prestadores da categoria
      let filtered = serviceProviders.filter(p => p.category_id === foundCategory.id);

      // Filtro por cidade
      if (filterCity) {
        filtered = filtered.filter(p => 
          p.city.toLowerCase().includes(filterCity.toLowerCase())
        );
      }

      // Ordenação
      if (sortBy === 'rating') {
        filtered.sort((a, b) => (b.average_rating * 100 + b.reviews_count) - (a.average_rating * 100 + a.reviews_count));
      } else if (sortBy === 'contracts') {
        filtered.sort((a, b) => b.total_contracts - a.total_contracts);
      }

      setProviders(filtered);
    }
  }, [categorySlug, sortBy, filterCity]);

  if (!category) {
    return (
      <div className="min-h-screen bg-aug-cream">
        <Header />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-aug-gray-dark">Categoria não encontrada</h1>
        </div>
      </div>
    );
  }

  const handleProviderClick = (providerId) => {
    navigate(`/${country}/services/${providerId}`);
  };

  const handleContactWhatsApp = (e, provider) => {
    e.stopPropagation();
    if (provider.whatsapp) {
      window.open(`https://wa.me/${provider.whatsapp}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          <Breadcrumbs
            items={[
              { label: 'Home', path: `/${country}` },
              { label: 'Serviços', path: `/${country}/services` },
              { label: category.name, path: `/${country}/services/category/${category.slug}` }
            ]}
          />

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-20 h-20 bg-aug-blue-light rounded-xl flex items-center justify-center text-4xl">
                    {category.icon}
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B5E20' }}>
                      {category.name}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-aug-gray-medium">
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <strong>{category.average_rating}</strong> avaliação média
                      </span>
                      <span>•</span>
                      <span><strong>{providers.length}</strong> profissionais</span>
                    </div>
                  </div>
                </div>
                <p className="text-lg text-aug-gray-medium">
                  {category.description}
                </p>
              </div>

              {/* Filters */}
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-sm font-medium text-aug-gray-dark block mb-1">
                    Ordenar por:
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-aug-gray-light rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-aug-blue"
                  >
                    <option value="rating">Melhor Avaliados</option>
                    <option value="contracts">Mais Contratados</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-aug-gray-dark block mb-1">
                    Filtrar por cidade:
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: São Paulo"
                    value={filterCity}
                    onChange={(e) => setFilterCity(e.target.value)}
                    className="px-4 py-2 border border-aug-gray-light rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-aug-blue"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Providers List */}
          {providers.length === 0 ? (
            <div className="bg-white rounded-xl p-16 text-center">
              <h2 className="text-2xl font-bold text-aug-gray-dark mb-2">
                Nenhum profissional encontrado
              </h2>
              <p className="text-aug-gray-medium mb-6">
                {filterCity 
                  ? `Não encontramos profissionais em "${filterCity}". Tente outra cidade.`
                  : 'Esta categoria ainda não possui profissionais cadastrados.'}
              </p>
              {filterCity && (
                <button
                  onClick={() => setFilterCity('')}
                  className="px-6 py-3 rounded-lg font-semibold text-white"
                  style={{ backgroundColor: '#1B5E20' }}
                >
                  Limpar Filtro
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map((provider) => (
                <div
                  key={provider.id}
                  onClick={() => handleProviderClick(provider.id)}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  {/* Header with Image */}
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={provider.profile_image}
                      alt={provider.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    
                    {/* Video Badge */}
                    {provider.videos.length > 0 && (
                      <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded flex items-center gap-1 text-xs">
                        <Play className="w-3 h-3" />
                        {provider.videos.length} {provider.videos.length === 1 ? 'vídeo' : 'vídeos'}
                      </div>
                    )}

                    {/* Verified Badge */}
                    {provider.verified && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-medium">
                        ✓ Verificado
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-aug-gray-dark mb-2 line-clamp-2 min-h-[3.5rem]">
                      {provider.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-aug-gray-medium mb-3 line-clamp-2">
                      {provider.description}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-3 pb-3 border-b border-aug-gray-light">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-bold">{provider.average_rating}</span>
                        <span className="text-xs text-aug-gray-medium">({provider.reviews_count})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Award className="w-4 h-4 text-aug-blue" />
                        <span className="text-sm font-semibold">{provider.total_contracts}</span>
                        <span className="text-xs text-aug-gray-medium">contratos</span>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-center gap-1 text-sm text-aug-gray-medium mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{provider.city} - {provider.state}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProviderClick(provider.id);
                        }}
                        className="flex-1 py-2 rounded-lg font-medium text-white transition-colors text-sm"
                        style={{ backgroundColor: '#1B5E20' }}
                      >
                        Ver Perfil
                      </button>
                      {provider.whatsapp && (
                        <button
                          onClick={(e) => handleContactWhatsApp(e, provider)}
                          className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServiceCategoryPage;
